import React from "react";
import { Block, flattenIOPS, IOPS, ROOT_BLOCK_ID, QUEUE_MAX_STORAGE } from "../../models/block";
import { Card, CardContent, Typography, Box, Tooltip } from "@mui/material";
import { getCompactNumberStr } from "../../utils/iops-utils";
import { ContentTypeChip, CONTENT_TYPE_MAP } from "./ContentTypeChip";

interface BlockInfoDisplayProps {
  block: Block;
  title: string;
  currentInputIOPS?: IOPS;
  currentOutputIOPS?: IOPS;
}

// Deterministic color for each op-content key
const IOPS_COLORS: { [key: string]: string } = {
  "read-static": "#1976d2",
  "read-dynamic": "#43a047",
  "read-static_media": "#81c784",
  "write-dynamic": "#ffa000",
  "write-static": "#e53935",
  "write-static_media": "#ffcc80",
};

function getIOPSColor(key: string) {
  if (CONTENT_TYPE_MAP[key]) return CONTENT_TYPE_MAP[key].color;
  if (IOPS_COLORS[key]) return IOPS_COLORS[key];
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = key.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue},70%,80%)`;
}

/** Returns a status color based on how close current is to capacity */
function getUtilizationColor(current: number, capacity: number): string {
  if (capacity <= 0) return "#9e9e9e";
  const ratio = current / capacity;
  if (ratio >= 1) return "#e53935";    // red — at or over capacity
  if (ratio >= 0.8) return "#ff9800";  // orange — nearing capacity
  if (ratio >= 0.5) return "#fbc02d";  // yellow — moderate load
  return "#43a047";                     // green — healthy
}

// ── Progress bar row (used for non-root blocks) ──────────────────────────────

const IOPSUsageBar = ({ label, current, capacity }: { label: string; current: number; capacity: number }) => {
  const percentage = capacity > 0 ? Math.min((current / capacity) * 100, 100) : 0;
  const color = getIOPSColor(label);

  return (
    <Box sx={{ mb: 1, minWidth: 250 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, alignItems: "center" }}>
        <ContentTypeChip typeKey={label} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {getCompactNumberStr(current)} / {getCompactNumberStr(capacity)} ({percentage.toFixed(1)}%)
        </Typography>
      </Box>
      <Box
        sx={{
          height: 8,
          backgroundColor: "#e0e0e0",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${percentage}%`,
            backgroundColor: color,
            transition: "width 0.3s ease",
          }}
        />
      </Box>
    </Box>
  );
};

// ── Raw IOPS row with color indicator (used for root/user block) ─────────────

const IOPSRow = ({ label, current, capacity }: { label: string; current: number; capacity: number }) => {
  const labelColor = getIOPSColor(label);
  const statusColor = capacity > 0 ? getUtilizationColor(current, capacity) : labelColor;

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
      <ContentTypeChip typeKey={label} />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: statusColor,
          minWidth: 45,
          textAlign: "right",
        }}
      >
        {getCompactNumberStr(current)}
      </Typography>
    </Box>
  );
};

// ── Capacity section with progress bars (non-root blocks) ────────────────────

const IOPSCapacitySection = ({
  title,
  currentIOPS,
  capacityIOPS,
}: {
  title: string;
  currentIOPS: IOPS | undefined;
  capacityIOPS: IOPS;
}) => {
  // Always show the section if there's capacity — even when no traffic is flowing
  if (capacityIOPS.total === 0) return null;

  const flatCurrent = currentIOPS ? flattenIOPS(currentIOPS) : { total: 0 };
  const flatCapacity = flattenIOPS(capacityIOPS);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      {Object.keys(flatCapacity).filter(k => k !== "total").map((key) => {
        const currentVal = (flatCurrent as any)[key] as number ?? 0;
        const capacityVal = flatCapacity[key] as number;
        return (
          <IOPSUsageBar
            key={key}
            label={key}
            current={currentVal}
            capacity={capacityVal}
          />
        );
      })}
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
        Total: {getCompactNumberStr(flatCurrent.total)} / {getCompactNumberStr(flatCapacity.total)}
      </Typography>
    </Box>
  );
};

// ── Raw IOPS section (root/user block) ───────────────────────────────────────

const IOPSRawSection = ({
  title,
  iops,
}: {
  title: string;
  iops: IOPS | undefined;
}) => {
  if (!iops || iops.total === 0) return null;

  const flat = flattenIOPS(iops);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      {Object.keys(flat)
        .filter((k) => k !== "total")
        .map((key) => {
          const val = flat[key] as number ?? 0;
          return (
            <IOPSRow
              key={key}
              label={key}
              current={val}
              capacity={0}
            />
          );
        })}
      {/* Total row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1,
          pt: 0.5,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 88 }}>
          Total
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, minWidth: 45, textAlign: "right" }}
        >
          {getCompactNumberStr(flat.total)}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Special display components for known dynamicProperties schemas ─────────

const ServerCountDisplay: React.FC<{ count: number }> = ({ count }) => {
  const maxVisible = 45;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
          ASG Server Count
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#2e7d32" }}>
          {count} Active
        </Typography>
      </Box>
      <Box
        sx={{
          p: 1,
          backgroundColor: "rgba(76, 175, 80, 0.04)",
          borderRadius: "6px",
          border: "1px solid rgba(76, 175, 80, 0.12)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(15, 12px)",
            gap: "4px",
            justifyContent: "start",
          }}
        >
          {Array.from({ length: Math.min(count, maxVisible) }).map((_, i) => (
            <Tooltip title={`Server Instance #${i + 1}`} key={i} arrow>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "3px",
                  backgroundColor: "#4caf50",
                  backgroundImage: "linear-gradient(135deg, #81c784 0%, #4caf50 100%)",
                  border: "1px solid #388e3c",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 0 2px rgba(76, 175, 80, 0.4)",
                  transition: "transform 0.15s ease",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "scale(1.25)",
                    boxShadow: "0 0 6px rgba(76, 175, 80, 0.8)",
                    borderColor: "#1b5e20",
                  },
                }}
              />
            </Tooltip>
          ))}
        </Box>
        {count > maxVisible && (
          <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block", fontWeight: 600 }}>
            +{count - maxVisible} more
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ScaleCountDisplay: React.FC<{ count: number }> = ({ count }) => {
  const maxVisible = 30;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Lambda Concurrency
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "#7b1fa2" }}>
          {count} Active
        </Typography>
      </Box>
      <Box
        sx={{
          p: 1,
          backgroundColor: "rgba(156, 39, 176, 0.04)",
          borderRadius: "6px",
          border: "1px solid rgba(156, 39, 176, 0.12)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(15, 12px)",
            gap: "4px",
            justifyContent: "start",
          }}
        >
          {Array.from({ length: Math.min(count, maxVisible) }).map((_, i) => (
            <Tooltip title={`Lambda Instance #${i + 1}`} key={i} arrow>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#9c27b0",
                  backgroundImage: "linear-gradient(135deg, #ba68c8 0%, #9c27b0 100%)",
                  border: "1px solid #7b1fa2",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1), 0 0 2px rgba(156, 39, 176, 0.4)",
                  transition: "transform 0.15s ease",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "scale(1.25)",
                    boxShadow: "0 0 6px rgba(156, 39, 176, 0.8)",
                    borderColor: "#4a148c",
                  },
                }}
              />
            </Tooltip>
          ))}
        </Box>
        {count > maxVisible && (
          <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block", fontWeight: 600 }}>
            +{count - maxVisible} more
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const QueueStateDisplay: React.FC<{ queueState: any }> = ({ queueState }) => {
  const total = queueState.total || 0;
  const capacity = QUEUE_MAX_STORAGE || 1000000;
  const percentage = (total / capacity) * 100;
  const progressColor = getUtilizationColor(total, capacity);
  const flatQueue = flattenIOPS(queueState as IOPS);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5, gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Queue Storage Backlog
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: progressColor }}>
          {getCompactNumberStr(total)} / {getCompactNumberStr(capacity)} ({percentage.toFixed(2)}%)
        </Typography>
      </Box>
      <Box
        sx={{
          height: 8,
          backgroundColor: "#e0e0e0",
          borderRadius: 1,
          overflow: "hidden",
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${Math.min(percentage, 100)}%`,
            backgroundColor: progressColor,
            transition: "width 0.3s ease",
          }}
        />
      </Box>

      {total > 0 && (
        <Box sx={{ pl: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.75 }}>
            Message Breakdown:
          </Typography>
          {Object.keys(flatQueue)
            .filter((key) => key !== "total" && flatQueue[key] > 0)
            .map((key) => {
              const val = flatQueue[key];
              return (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <ContentTypeChip typeKey={key} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary" }}>
                    {getCompactNumberStr(val)}
                  </Typography>
                </Box>
              );
            })}
        </Box>
      )}
    </Box>
  );
};

// ── Interactive JSON Tree View for all other schemas ────────────────────────

interface JSONNodeProps {
  name?: string;
  value: any;
  isLast?: boolean;
}

const JSONNode: React.FC<JSONNodeProps> = ({ name, value, isLast }) => {
  const [isExpanded, setIsExpanded] = React.useState(true);

  const isObject = value !== null && typeof value === "object";
  const isArray = Array.isArray(value);

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const keyColor = "#881391";
  const stringColor = "#c41a16";
  const numberColor = "#1c00cf";
  const booleanColor = "#0d5c11";
  const nullColor = "#777777";

  const renderValue = (val: any) => {
    if (typeof val === "string") {
      return <span style={{ color: stringColor }}>"{val}"</span>;
    }
    if (typeof val === "number") {
      return <span style={{ color: numberColor }}>{val}</span>;
    }
    if (typeof val === "boolean") {
      return <span style={{ color: booleanColor }}>{val ? "true" : "false"}</span>;
    }
    if (val === null) {
      return <span style={{ color: nullColor }}>null</span>;
    }
    return <span>{String(val)}</span>;
  };

  if (isObject) {
    const keys = Object.keys(value);
    const bracketOpen = isArray ? "[" : "{";
    const bracketClose = isArray ? "]" : "}";

    if (keys.length === 0) {
      return (
        <Box sx={{ pl: 2, fontFamily: "monospace", fontSize: "11px", lineHeight: 1.5 }}>
          {name && <span style={{ color: keyColor, fontWeight: 500 }}>{name}: </span>}
          <span>{bracketOpen}{bracketClose}</span>{!isLast && ","}
        </Box>
      );
    }

    return (
      <Box sx={{ pl: 2, fontFamily: "monospace", fontSize: "11px", lineHeight: 1.5 }}>
        <Box
          onClick={toggleExpand}
          sx={{
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            userSelect: "none",
            "&:hover": { opacity: 0.7 }
          }}
        >
          <span
            style={{
              fontSize: "8px",
              marginRight: "4px",
              display: "inline-block",
              transform: isExpanded ? "rotate(90deg)" : "none",
              transition: "transform 0.15s ease",
              color: "#666"
            }}
          >
            ▶
          </span>
          {name && <span style={{ color: keyColor, fontWeight: 500, marginRight: "4px" }}>{name}:</span>}
          <span style={{ color: "#333", fontWeight: 600 }}>{bracketOpen}</span>
          {!isExpanded && (
            <span style={{ color: "#888", fontSize: "10px", margin: "0 4px", fontStyle: "italic" }}>
              ... {keys.length} items ...
            </span>
          )}
          {!isExpanded && <span style={{ color: "#333", fontWeight: 600 }}>{bracketClose}</span>}
        </Box>
        {isExpanded && (
          <Box sx={{ borderLeft: "1px dashed #dcdcdc", ml: 0.6, pl: 1 }}>
            {keys.map((key, idx) => (
              <JSONNode
                key={key}
                name={isArray ? undefined : key}
                value={value[key]}
                isLast={idx === keys.length - 1}
              />
            ))}
          </Box>
        )}
        {isExpanded && (
          <Box sx={{ pl: 1.5 }}>
            <span style={{ color: "#333", fontWeight: 600 }}>{bracketClose}</span>{!isLast && ","}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ pl: 2, fontFamily: "monospace", fontSize: "11px", lineHeight: 1.5 }}>
      {name && <span style={{ color: keyColor, fontWeight: 500 }}>{name}: </span>}
      {renderValue(value)}
      {!isLast && ","}
    </Box>
  );
};

const JSONTreeView: React.FC<{ data: any }> = ({ data }) => {
  return (
    <Box
      sx={{
        p: 1.5,
        backgroundColor: "#fafdff",
        borderRadius: "6px",
        border: "1px solid #e1e4e8",
        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.02)",
        overflowX: "auto",
        maxHeight: "300px"
      }}
    >
      <JSONNode value={data} isLast={true} />
    </Box>
  );
};

export const BlockInfoDisplay: React.FC<BlockInfoDisplayProps> = ({ block, title, currentInputIOPS, currentOutputIOPS }) => {
  const isRootBlock = block.id === ROOT_BLOCK_ID;

  return (
    <Card
      style={{
        marginLeft: 12,
        minWidth: 250,
        background: "#f7f7fa",
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        fontSize: 12,
      }}
      elevation={2}
    >
      <CardContent style={{ padding: 12 }}>
        <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 12 }}>
          {title}
        </Typography>

        {isRootBlock ? (
          /* Root/User block: show raw output IOPS values */
          <IOPSRawSection
            title="Output TPS"
            iops={currentOutputIOPS}
          />
        ) : (
          /* Non-root blocks: show capacity with progress bars */
          <>
            <IOPSCapacitySection
              title="Input Capacity"
              currentIOPS={currentInputIOPS}
              capacityIOPS={block.inputCapacity}
            />

            {block.processingCapacity && block.processingCapacity.total > 0 && (
              <IOPSCapacitySection
                title="Processing Capacity"
                currentIOPS={currentInputIOPS}
                capacityIOPS={block.processingCapacity}
              />
            )}
          </>
        )}

        {block.dynamicProperties && Object.keys(block.dynamicProperties).length > 0 && (
          <Box mt={2} pt={1.5} sx={{ borderTop: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Dynamic Properties
            </Typography>
            {(() => {
              const props = block.dynamicProperties!;
              const hasServerCount = "server-count" in props;
              const hasScaleCount = "scale-count" in props;
              const hasQueueState = "queueState" in props;

              if (hasServerCount || hasScaleCount || hasQueueState) {
                return (
                  <Box>
                    {hasServerCount && <ServerCountDisplay count={props["server-count"]} />}
                    {hasScaleCount && <ScaleCountDisplay count={props["scale-count"]} />}
                    {hasQueueState && <QueueStateDisplay queueState={props["queueState"]} />}
                  </Box>
                );
              }

              // Fallback to interactive JSON tree view for other blocks
              return <JSONTreeView data={props} />;
            })()}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
